import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { getLevelFromPoints, getLevelTitle } from '../lib/scores'

export default function ProfilePage({ onNavigate }) {
  const { user, profile, signOut } = useAuth()
  const [stats, setStats] = useState({ totalDays: 0, mostActiveArea: '—', avgScore: 0 })
  const [totalPoints, setTotalPoints] = useState(0)
  const [editingGoals, setEditingGoals] = useState(false)
  const [selectedGoals, setSelectedGoals] = useState(profile?.goals || [])
  const [journalStats, setJournalStats] = useState({ topPeople: [], avgRating: null })

  useEffect(() => {
    if (user) { loadStats(); loadJournalStats() }
  }, [user])

  async function loadJournalStats() {
    const { data } = await supabase
      .from('journal_entries')
      .select('spent_time_with, day_rating')
      .eq('user_id', user.id)
    if (!data || data.length === 0) return
    const mentions = {}
    data.forEach(d => {
      if (d.spent_time_with) {
        d.spent_time_with.split(/[,&]+/).map(n => n.trim()).filter(n => n.length > 1).forEach(name => {
          const key = name.toLowerCase()
          mentions[key] = (mentions[key] || 0) + 1
        })
      }
    })
    const topPeople = Object.entries(mentions).sort((a, b) => b[1] - a[1]).slice(0, 3)
    const ratings = data.filter(d => d.day_rating).map(d => d.day_rating)
    const avgRating = ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : null
    setJournalStats({ topPeople, avgRating })
  }

  async function loadStats() {
    // Load all manual tasks for days logged + most active area
    const { data: tasks } = await supabase
      .from('manual_tasks')
      .select('area, final_points, date')
      .eq('user_id', user.id)
      .eq('flagged', false)

    if (tasks && tasks.length > 0) {
      // Group by date and calculate daily overall score
      const byDate = {}
      tasks.forEach(t => {
        if (!byDate[t.date]) byDate[t.date] = { health: 0, mind: 0, career: 0, relationships: 0, faith: 0, purpose: 0 }
        if (byDate[t.date][t.area] !== undefined) byDate[t.date][t.area] += t.final_points
      })

      // Daily overall = average of all 6 areas, capped at 100
      const dailyOveralls = Object.values(byDate).map(d => {
        const areas = [d.health, d.mind, d.career, d.relationships, d.faith, d.purpose]
        return Math.min(100, Math.round(areas.reduce((a, b) => a + b, 0) / 6))
      })

      // Level = sum of daily overall scores
      const totalOverallPoints = dailyOveralls.reduce((a, b) => a + b, 0)
      setTotalPoints(totalOverallPoints)

      const areaTotals = {}
      tasks.forEach(t => {
        areaTotals[t.area] = (areaTotals[t.area] || 0) + (t.final_points || 0)
      })
      const areaLabels = { health: 'Health', mind: 'Mind', career: 'Career', relationships: 'Relationships', faith: 'Faith', purpose: 'Goals' }
      const mostActive = Object.entries(areaTotals).sort((a, b) => b[1] - a[1])[0]?.[0] || 'purpose'

      setStats(prev => ({
        ...prev,
        totalDays: Object.keys(byDate).length,
        mostActiveArea: areaLabels[mostActive] || '—',
      }))
    }

    // Average score from daily overalls
    const { data: allTasks } = await supabase
      .from('manual_tasks')
      .select('area, final_points, date')
      .eq('user_id', user.id)
      .eq('flagged', false)

    if (allTasks && allTasks.length > 0) {
      const byDate2 = {}
      allTasks.forEach(t => {
        if (!byDate2[t.date]) byDate2[t.date] = { health: 0, mind: 0, career: 0, relationships: 0, faith: 0, purpose: 0 }
        if (byDate2[t.date][t.area] !== undefined) byDate2[t.date][t.area] += t.final_points
      })
      const dailyScores = Object.values(byDate2).map(d => {
        const areas = [d.health, d.mind, d.career, d.relationships, d.faith, d.purpose]
        return Math.min(100, Math.round(areas.reduce((a, b) => a + b, 0) / 6))
      })
      if (dailyScores.length > 0) {
        const avg = Math.round(dailyScores.reduce((a, b) => a + b, 0) / dailyScores.length)
        setStats(prev => ({ ...prev, avgScore: avg }))
      }
    }
  }

  const GOALS = [
    { key: 'business', label: 'Build a Business', icon: '🚀' },
    { key: 'fitness', label: 'Get Fit / Lose Weight', icon: '💪' },
    { key: 'academic', label: 'Academic Excellence', icon: '📚' },
    { key: 'career', label: 'Career Advancement', icon: '📈' },
    { key: 'financial', label: 'Financial Freedom', icon: '💰' },
    { key: 'spiritual', label: 'Spiritual Growth', icon: '🙏' },
    { key: 'creative', label: 'Creative Pursuit', icon: '🎨' },
    { key: 'athletic', label: 'Athletic Performance', icon: '🏆' },
  ]

  function toggleGoal(key) {
    if (selectedGoals.includes(key)) setSelectedGoals(selectedGoals.filter(g => g !== key))
    else if (selectedGoals.length < 3) setSelectedGoals([...selectedGoals, key])
  }

  async function saveGoals() {
    await supabase.from('profiles').update({ goals: selectedGoals }).eq('id', user.id)
    setEditingGoals(false)
  }

  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || null)
  const [showEditProfile, setShowEditProfile] = useState(false)
  const [editName, setEditName] = useState(profile?.full_name || '')
  const [editEmail, setEditEmail] = useState(user?.email || '')
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const avatarInputRef = React.useRef(null)

  useEffect(() => {
    if (profile?.avatar_url) setAvatarUrl(profile.avatar_url)
    if (profile?.full_name) setEditName(profile.full_name)
    if (user?.email) setEditEmail(user.email)
  }, [profile, user])

  const [cropSrc, setCropSrc] = useState(null)
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 })
  const [cropScale, setCropScale] = useState(1)
  const [cropDragging, setCropDragging] = useState(false)
  const [cropDragStart, setCropDragStart] = useState(null)
  const [cropLastDist, setCropLastDist] = useState(null)
  const cropImgRef = React.useRef(null)
  const CROP_SIZE = 280

  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0]
    if (!file || !user) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      setCropSrc(ev.target.result)
      setCropOffset({ x: 0, y: 0 })
      setCropScale(1)
    }
    reader.readAsDataURL(file)
  }

  function handleCropDragStart(e) {
    e.preventDefault()
    if (e.touches?.length === 2) return
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    setCropDragging(true)
    setCropDragStart({ x: clientX - cropOffset.x, y: clientY - cropOffset.y })
  }

  function handleCropDragMove(e) {
    e.preventDefault()
    if (e.touches?.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (cropLastDist) {
        const delta = dist / cropLastDist
        setCropScale(prev => Math.min(4, Math.max(0.3, prev * delta)))
      }
      setCropLastDist(dist)
      return
    }
    if (!cropDragging || !cropDragStart) return
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    setCropOffset({ x: clientX - cropDragStart.x, y: clientY - cropDragStart.y })
  }

  function handleCropDragEnd() {
    setCropDragging(false)
    setCropLastDist(null)
  }

  async function saveCroppedAvatar() {
    const img = cropImgRef.current
    if (!img || !user) return
    setUploadingAvatar(true)
    setEditError('')
    try {
      const canvas = document.createElement('canvas')
      const size = 400
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')
      ctx.beginPath()
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
      ctx.clip()

      // Use the actual rendered size of the image (not natural size)
      const renderedW = CROP_SIZE * cropScale
      const renderedH = (img.naturalHeight / img.naturalWidth) * CROP_SIZE * cropScale

      // Scale everything up to canvas size
      const ratio = size / CROP_SIZE
      const drawW = renderedW * ratio
      const drawH = renderedH * ratio
      const drawX = size / 2 - drawW / 2 + cropOffset.x * ratio
      const drawY = size / 2 - drawH / 2 + cropOffset.y * ratio

      ctx.drawImage(img, drawX, drawY, drawW, drawH)

      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92))
      const path = `${user.id}/avatar.jpg`
      const { error: uploadError } = await supabase.storage.from('avatars').upload(path, blob, { upsert: true, contentType: 'image/jpeg' })
      if (uploadError) { setEditError('Upload failed. Try again.'); setUploadingAvatar(false); return }
      const { data } = supabase.storage.from('avatars').getPublicUrl(path)
      const publicUrl = data.publicUrl + '?t=' + Date.now()
      await supabase.from('profiles').update({ avatar_url: publicUrl }).eq('id', user.id)
      setAvatarUrl(publicUrl)
      setCropSrc(null)
    } catch (e) { setEditError('Something went wrong.') }
    setUploadingAvatar(false)
  }

  async function saveEditProfile() {
    if (!editName.trim()) { setEditError('Name cannot be empty'); return }
    setEditSaving(true)
    setEditError('')
    try {
      await supabase.from('profiles').update({ full_name: editName.trim() }).eq('id', user.id)
      if (editEmail.trim() !== user.email) {
        const { error } = await supabase.auth.updateUser({ email: editEmail.trim() })
        if (error) { setEditError('Could not update email: ' + error.message); setEditSaving(false); return }
      }
      setShowEditProfile(false)
    } catch (e) { setEditError('Something went wrong.') }
    setEditSaving(false)
  }

  async function handleSignOut() {
    await signOut()
    window.location.href = '/auth'
  }

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?'

  const { level, pointsIntoLevel, pointsRequired, progress } = getLevelFromPoints(totalPoints)
  const levelTitle = getLevelTitle(level)

  return (
    <div style={{ ...s.page, background: '#111118', color: '#e8e6df' }}>
      <div style={s.ambient} />

      <div style={{ ...s.header, background: 'rgba(6,6,8,0.85)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <div style={{ ...s.backBtn, background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)' }} onClick={() => onNavigate('dashboard')}>←</div>
        <div style={s.headerTitle}>Profile</div>
        <div style={{ width: 36 }} />
      </div>

      <div style={s.main}>

        {/* AVATAR / HERO */}
        <div style={s.profileHero}>
          <div style={s.avatarWrap}>
            {avatarUrl
              ? <img src={avatarUrl} alt="avatar" style={s.avatarImg} />
              : <div style={s.avatar}>{initials}</div>
            }
          </div>
          <div style={{ ...s.name, color: '#e8e6df' }}>{profile?.full_name || 'User'}</div>
          <div style={{ ...s.email, color: '#6b6860' }}>{user?.email}</div>
          <div style={s.editProfileBtn} onClick={() => setShowEditProfile(true)}>Edit Profile</div>
        </div>

        {/* EDIT PROFILE MODAL */}
        {showEditProfile && (
          <>
            <div style={s.modalOverlay} onClick={() => setShowEditProfile(false)} />
            <div style={s.modal}>
              <div style={s.modalTitle}>Edit Profile</div>

              {/* AVATAR UPLOAD */}
              <div style={s.modalAvatarRow}>
                <div style={s.modalAvatarWrap} onClick={() => avatarInputRef.current?.click()}>
                  {avatarUrl
                    ? <img src={avatarUrl} alt="avatar" style={s.modalAvatarImg} />
                    : <div style={s.modalAvatar}>{initials}</div>
                  }
                  <div style={s.modalAvatarBadge}>
                    {uploadingAvatar ? '...' : '📷'}
                  </div>
                </div>
                <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
                <div style={s.modalAvatarLabel}>
                  <div style={s.modalAvatarHint}>Tap photo to change</div>
                  {uploadingAvatar && <div style={{ fontSize: '12px', color: '#c9a84c' }}>Uploading...</div>}
                </div>
              </div>

              {/* CROP TOOL */}
              {/* NAME */}
              <div style={s.modalField}>
                <div style={s.modalLabel}>Full Name</div>
                <input
                  style={s.modalInput}
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  placeholder="Your name"
                />
              </div>

              {/* EMAIL */}
              <div style={s.modalField}>
                <div style={s.modalLabel}>Email</div>
                <input
                  style={s.modalInput}
                  value={editEmail}
                  onChange={e => setEditEmail(e.target.value)}
                  placeholder="your@email.com"
                  autoCapitalize="none"
                  autoCorrect="off"
                />
                <div style={s.modalHint}>Changing email requires verification</div>
              </div>

              {editError ? <div style={s.modalError}>{editError}</div> : null}

              <div style={s.modalActions}>
                <div style={s.modalCancel} onClick={() => { setShowEditProfile(false); setEditError('') }}>Cancel</div>
                <div style={{ ...s.modalSave, opacity: editSaving ? 0.6 : 1 }} onClick={saveEditProfile}>
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </div>
              </div>
            </div>
          </>
        )}

        {/* FULL SCREEN CROP OVERLAY */}
        {cropSrc && (
          <div style={s.cropOverlay}>
            <div style={s.cropHeader}>
              <div style={s.cropTitle}>Adjust Photo</div>
              <div style={s.cropSubtitle}>Drag to move · Pinch to zoom</div>
            </div>
            <div style={s.cropCircleWrap}
              onMouseDown={handleCropDragStart}
              onMouseMove={handleCropDragMove}
              onMouseUp={handleCropDragEnd}
              onTouchStart={handleCropDragStart}
              onTouchMove={handleCropDragMove}
              onTouchEnd={handleCropDragEnd}>
              <img ref={cropImgRef} src={cropSrc} alt="crop"
                draggable={false}
                style={{
                  position: 'absolute',
                  top: '50%', left: '50%',
                  transform: `translate(calc(-50% + ${cropOffset.x}px), calc(-50% + ${cropOffset.y}px)) scale(${cropScale})`,
                  transformOrigin: 'center',
                  userSelect: 'none',
                  pointerEvents: 'none',
                  maxWidth: 'none',
                  width: `${CROP_SIZE}px`,
                }}
              />
              <div style={s.cropRing} />
            </div>
            <div style={s.cropButtons}>
              <div style={s.cropCancelBtn} onClick={() => { avatarInputRef.current.value = ''; avatarInputRef.current.click() }}>
                Use Different Photo
              </div>
              <div style={{ ...s.cropConfirmBtn, opacity: uploadingAvatar ? 0.6 : 1 }} onClick={saveCroppedAvatar}>
                {uploadingAvatar ? 'Saving...' : 'Confirm ✓'}
              </div>
            </div>
          </div>
        )}

        {/* LEVEL CARD */}
        <div style={s.section}>
          <div style={{ ...s.sectionTitle, color: '#6b6860' }}>Level</div>
          <div style={{ ...s.levelCard, background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)' }}>
            <div style={s.levelTop}>
              <div style={s.levelLeft}>
                <div style={s.levelNum}>Level {level}</div>
                <div style={s.levelTitle}>{levelTitle}</div>
              </div>
              <div style={s.levelRight}>
                <div style={s.levelPoints}>{totalPoints.toLocaleString()}</div>
                <div style={{ ...s.levelPointsSub, color: '#6b6860' }}>total pts</div>
              </div>
            </div>
            <div style={{ ...s.levelBarBg, background: 'rgba(255,255,255,0.12)' }}>
              <div style={{ ...s.levelBarFill, width: `${progress * 100}%` }} />
            </div>
            <div style={{ ...s.levelProgress, color: '#6b6860' }}>
              {pointsIntoLevel} / {pointsRequired} pts to Level {level + 1}
            </div>
          </div>
        </div>

        {/* STATS */}
        <div style={s.section}>
          <div style={{ ...s.sectionTitle, color: '#6b6860' }}>Your Stats</div>
          <div style={s.statsGrid}>
            {[
              { val: profile?.streak || 0, label: 'Current Streak' },
              { val: stats.totalDays, label: 'Days Logged' },
              { val: stats.avgScore || 0, label: 'Average Score' },
              { val: stats.mostActiveArea, label: 'Top Area', small: true },
            ].map(({ val, label, small }) => (
              <div key={label} style={{ ...s.statCard, background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)' }}>
                <div style={{ ...s.statVal, fontSize: small ? '18px' : '36px', paddingTop: small ? '6px' : 0, color: '#f0cc7a' }}>{val}</div>
                <div style={{ ...s.statLabel, color: '#6b6860' }}>{label}</div>
              </div>
            ))}
          </div>

          {/* JOURNAL STATS */}
          <div style={{ ...s.journalStatsCard, background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)' }}>
            <div style={{ ...s.sectionTitle, color: '#6b6860', marginBottom: '14px' }}>Journal Insights</div>
            <div style={s.journalStatsRow}>
              <div style={s.journalStatCol}>
                <div style={{ ...s.statLabel, color: '#6b6860', marginBottom: '10px' }}>Most time with</div>
                {journalStats.topPeople.length > 0
                  ? journalStats.topPeople.map(([name, count]) => (
                      <div key={name} style={s.personRow}>
                        <span style={{ fontSize: '13px', color: '#e8e6df' }}>{name.charAt(0).toUpperCase() + name.slice(1)}</span>
                        <span style={{ fontSize: '11px', color: '#c9a84c', background: 'rgba(201,168,76,0.1)', padding: '2px 7px', borderRadius: '6px' }}>{count}x</span>
                      </div>
                    ))
                  : <div style={{ fontSize: '12px', color: '#6b6860', fontStyle: 'italic' }}>Log journal entries to see who you spend the most time with</div>
                }
              </div>
              <div style={s.journalDivider} />
              <div style={s.journalStatCol}>
                <div style={{ ...s.statLabel, color: '#6b6860', marginBottom: '8px' }}>Avg day rating</div>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', color: '#f0cc7a', lineHeight: 1 }}>{journalStats.avgRating || '—'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* SETTINGS */}
        <div style={s.section}>
          <div style={{ ...s.sectionTitle, color: '#6b6860' }}>Settings</div>
          <div style={{ ...s.settingsCard, background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)' }}>

            {/* CHANGE GOALS */}
            <div style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
              <div style={s.settingRow} onClick={() => setEditingGoals(!editingGoals)}>
                <div style={{ ...s.settingText, color: '#e8e6df' }}>Your Main Goals</div>
                <div style={{ color: '#c9a84c' }}>{editingGoals ? '↑ Done' : 'Edit →'}</div>
              </div>
              {!editingGoals && profile?.goals?.length > 0 && (
                <div style={s.currentGoals}>
                  {profile.goals.map(g => {
                    const goal = GOALS.find(gl => gl.key === g)
                    return goal ? <div key={g} style={s.goalTag}>{goal.icon} {goal.label}</div> : null
                  })}
                </div>
              )}
              {editingGoals && (
                <div style={s.goalEditor}>
                  <div style={s.goalEditorSub}>Pick up to 3 goals</div>
                  {GOALS.map(goal => {
                    const selected = selectedGoals.includes(goal.key)
                    const disabled = !selected && selectedGoals.length >= 3
                    return (
                      <div key={goal.key} style={{ ...s.goalEditorRow, opacity: disabled ? 0.4 : 1, background: selected ? 'rgba(201,168,76,0.08)' : 'transparent' }}
                        onClick={() => !disabled && toggleGoal(goal.key)}>
                        <div style={s.goalEditorIcon}>{goal.icon}</div>
                        <div style={{ ...s.goalEditorLabel, color: '#e8e6df' }}>{goal.label}</div>
                        {selected && <div style={s.goalEditorCheck}>✓</div>}
                      </div>
                    )
                  })}
                  <div style={s.saveGoalsBtn} onClick={saveGoals}>Save Goals</div>
                </div>
              )}
            </div>

            {/* EMAIL */}
            <div style={{ ...s.settingRow, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
              <div style={{ ...s.settingText, color: '#e8e6df' }}>Email</div>
              <div style={{ ...s.settingValue, color: '#6b6860' }}>{user?.email}</div>
            </div>

            {/* SIGN OUT */}
            <div style={{ ...s.settingRow, borderTop: '1px solid rgba(255,255,255,0.12)' }} onClick={() => {
                localStorage.removeItem(`lifescore_tutorial_${user?.id}`)
                alert('Tutorial reset! Go back to the dashboard to see it.')
              }}>
              <div style={{ ...s.settingText, color: '#c9a84c' }}>Reset Tutorial</div>
            </div>
            <div style={{ ...s.settingRow, borderTop: '1px solid rgba(255,255,255,0.12)' }} onClick={handleSignOut}>
              <div style={{ ...s.settingText, color: '#e05c5c' }}>Sign Out</div>
              <div style={{ color: '#e05c5c' }}>→</div>
            </div>

          </div>
        </div>

        <div style={{ ...s.footer, color: 'rgba(128,128,128,0.3)' }}>LifeScore · Built with Claude</div>

      </div>
    </div>
  )
}

const s = {
  page: { minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", position: 'relative', overflowX: 'hidden' },
  ambient: { position: 'fixed', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '800px', background: 'radial-gradient(ellipse, rgba(201,168,76,0.22) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  header: { position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'max(18px, env(safe-area-inset-top))', paddingBottom: '18px', paddingLeft: '24px', paddingRight: '24px', backdropFilter: 'blur(16px)' },
  backBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', cursor: 'pointer', fontSize: '18px', color: '#c9a84c' },
  headerTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '3px', color: '#c9a84c' },
  main: { maxWidth: '420px', margin: '0 auto', padding: '24px 20px 100px', position: 'relative', zIndex: 1 },
  profileHero: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0 32px', gap: '8px' },
  avatarWrap: { position: 'relative', width: '80px', height: '80px', marginBottom: '4px' },
  avatarImg: { width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(201,168,76,0.3)' },
  avatar: { width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', fontWeight: '700', color: '#000' },
  editProfileBtn: { marginTop: '10px', fontSize: '12px', color: '#c9a84c', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '6px 16px', cursor: 'pointer', fontWeight: '500' },
  // Modal
  cropOverlay: { position: 'fixed', inset: 0, background: '#0d0d14', zIndex: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '28px', padding: '40px 24px' },
  cropHeader: { textAlign: 'center' },
  cropTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '3px', color: '#e8e6df' },
  cropSubtitle: { fontSize: '13px', color: '#6b6860', marginTop: '4px' },
  cropCircleWrap: { position: 'relative', width: '280px', height: '280px', borderRadius: '50%', overflow: 'hidden', background: '#111', cursor: 'grab', flexShrink: 0, touchAction: 'none' },
  cropRing: { position: 'absolute', inset: 0, borderRadius: '50%', boxShadow: 'inset 0 0 0 3px rgba(201,168,76,0.6)', pointerEvents: 'none', zIndex: 2 },
  cropButtons: { display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', maxWidth: '320px' },
  cropConfirmBtn: { padding: '16px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', borderRadius: '14px', textAlign: 'center', cursor: 'pointer', fontSize: '16px', fontWeight: '700', fontFamily: "'DM Sans', sans-serif" },
  cropCancelBtn: { padding: '14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#6b6860', borderRadius: '14px', textAlign: 'center', cursor: 'pointer', fontSize: '14px' },
  modal: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '24px 24px 0 0', padding: '28px 24px 48px', zIndex: 301, display: 'flex', flexDirection: 'column', gap: '16px' },
  modalTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '2px', color: '#e8e6df', marginBottom: '4px' },
  modalAvatarRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  modalAvatarWrap: { position: 'relative', width: '64px', height: '64px', flexShrink: 0, cursor: 'pointer' },
  modalAvatarImg: { width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(201,168,76,0.3)' },
  modalAvatar: { width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: '700', color: '#000' },
  modalAvatarBadge: { position: 'absolute', bottom: 0, right: 0, width: '22px', height: '22px', borderRadius: '50%', background: '#111118', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px' },
  modalAvatarLabel: { flex: 1 },
  modalAvatarHint: { fontSize: '13px', color: '#6b6860' },
  modalField: { display: 'flex', flexDirection: 'column', gap: '6px' },
  modalLabel: { fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860' },
  modalInput: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', padding: '13px 16px', color: '#e8e6df', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box' },
  modalHint: { fontSize: '11px', color: '#6b6860' },
  modalError: { fontSize: '13px', color: '#e05c5c', background: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '8px', padding: '10px 14px' },
  modalActions: { display: 'flex', gap: '10px', marginTop: '4px' },
  modalCancel: { flex: 1, padding: '13px', background: 'rgba(255,255,255,0.05)', color: '#6b6860', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', fontSize: '14px' },
  modalSave: { flex: 2, padding: '13px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', fontSize: '14px', fontWeight: '700' },
  name: { fontFamily: "'DM Serif Display', serif", fontSize: '22px' },
  email: { fontSize: '13px' },
  proBadge: { background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', fontSize: '11px', fontWeight: '700', letterSpacing: '2px', padding: '4px 12px', borderRadius: '20px', marginTop: '4px' },
  section: { marginBottom: '24px' },
  sectionTitle: { fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '12px' },
  levelCard: { borderRadius: '16px', padding: '20px' },
  levelTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  levelLeft: {},
  levelNum: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', color: '#c9a84c', letterSpacing: '2px', lineHeight: 1 },
  levelTitle: { fontSize: '13px', color: '#6b6860', marginTop: '2px' },
  levelRight: { textAlign: 'right' },
  levelPoints: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: '#f0cc7a', lineHeight: 1 },
  levelPointsSub: { fontSize: '11px', marginTop: '2px' },
  levelBarBg: { height: '6px', borderRadius: '3px', overflow: 'hidden', marginBottom: '8px' },
  levelBarFill: { height: '100%', borderRadius: '3px', background: 'linear-gradient(90deg, #8b6a1a, #f0cc7a)', transition: 'width 1s ease' },
  levelProgress: { fontSize: '11px', textAlign: 'right' },
  statsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' },
  journalStatsCard: { borderRadius: '14px', padding: '16px', marginTop: '10px' },
  journalStatsRow: { display: 'flex', gap: '16px' },
  journalStatCol: { flex: 1 },
  journalDivider: { width: '1px', background: 'rgba(255,255,255,0.08)', flexShrink: 0 },
  personRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  statCard: { borderRadius: '14px', padding: '20px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' },
  statVal: { fontFamily: "'Bebas Neue', sans-serif", lineHeight: 1 },
  statLabel: { fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase' },
  proCard: { background: 'linear-gradient(135deg, #1a1506, #1a1a26)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '16px', padding: '22px 20px' },
  proCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  proTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '3px', color: '#f0cc7a' },
  proPrice: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: '#c9a84c' },
  proFeatures: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '18px' },
  proFeat: { display: 'flex', alignItems: 'center', gap: '10px' },
  proFeatCheck: { width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#c9a84c', flexShrink: 0 },
  proFeatText: { fontSize: '13px' },
  proYearly: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', background: 'rgba(76,175,125,0.08)', border: '1px solid rgba(76,175,125,0.2)', borderRadius: '10px', padding: '8px 12px' },
  proYearlyBadge: { fontSize: '9px', fontWeight: '700', letterSpacing: '1.5px', color: '#4caf7d', background: 'rgba(76,175,125,0.15)', padding: '3px 7px', borderRadius: '5px' },
  proYearlyText: { fontSize: '13px', color: '#4caf7d', fontWeight: '500' },
  proBtn: { width: '100%', padding: '14px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', marginBottom: '8px', boxSizing: 'border-box' },
  proBtnYearly: { width: '100%', padding: '12px', background: 'rgba(201,168,76,0.08)', color: '#c9a84c', fontSize: '13px', fontWeight: '600', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', boxSizing: 'border-box' },
  settingsCard: { borderRadius: '16px', overflow: 'hidden' },
  settingRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', cursor: 'pointer' },
  settingText: { fontSize: '14px' },
  settingValue: { fontSize: '12px' },
  toggle: { width: '44px', height: '25px', borderRadius: '13px', position: 'relative', cursor: 'pointer', transition: 'background 0.3s', flexShrink: 0 },
  toggleThumb: { position: 'absolute', width: '19px', height: '19px', borderRadius: '50%', background: '#fff', top: '3px', left: '3px', transition: 'transform 0.3s', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' },
  footer: { textAlign: 'center', fontSize: '11px', marginTop: '20px' },
  settingLeft: { display: 'flex', flexDirection: 'column', gap: '3px' },
  proLock: { fontSize: '11px', color: '#c9a84c', opacity: 0.7 },
  currentGoals: { paddingBottom: '12px', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '6px' },
  goalTag: { fontSize: '13px', color: '#6b6860' },
  goalEditor: { padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '4px' },
  goalEditorSub: { fontSize: '11px', color: '#6b6860', marginBottom: '8px', letterSpacing: '1px', textTransform: 'uppercase' },
  goalEditorRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', cursor: 'pointer' },
  goalEditorIcon: { fontSize: '18px' },
  goalEditorLabel: { flex: 1, fontSize: '13px' },
  goalEditorCheck: { width: '18px', height: '18px', borderRadius: '50%', background: '#c9a84c', color: '#000', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  saveGoalsBtn: { marginTop: '8px', padding: '12px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', borderRadius: '10px', textAlign: 'center', cursor: 'pointer', fontSize: '14px', fontWeight: '700' },
}
